import { getLocales } from "expo-localization";
import { I18n } from "i18n-js";
import type { PropsWithChildren } from "react";
import React, { createContext, useLayoutEffect, useMemo } from "react";

import TRANSLATIONS from "$src/core/constants/translations";
import useConfig from "$src/core/hooks/use-config";
import useTranslationsQuery from "$src/core/queries/use-translations-query";

type TranslationContextType = {
  i18n: I18n;
};

// eslint-disable-next-line import/no-mutable-exports
export let i18n = new I18n({ en: TRANSLATIONS });

i18n.enableFallback = true;

export const TranslationContext = createContext<TranslationContextType | null>(
  null,
);

const TranslationProvider = ({ children }: PropsWithChildren) => {
  const { intl } = useConfig();

  const { data: translations } = useTranslationsQuery();

  const instance = useMemo(() => {
    if (!translations) return i18n;

    const locale =
      getLocales().at(0)?.languageCode ??
      Object.keys(translations).at(0) ??
      intl.defaultLocale;

    const next = new I18n({ [locale]: { ...TRANSLATIONS, ...translations } });
    next.defaultLocale = locale;
    next.locale = locale;
    next.enableFallback = true;

    return next;
  }, [translations, intl.defaultLocale]);

  useLayoutEffect(() => {
    i18n = instance;
  }, [instance]);

  const value = useMemo(() => ({ i18n: instance }), [instance]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationProvider;
